import { Campaign, Loader, LogService, Overlay, SelectorImage, ToastHelper, UserInDetails, Video, VisionService } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Footer from './components/footer/footer'
import Header from './components/header/header'
import './overlay-content-video.component.scoped.scss'
import { SqLoader } from '@squidit/react-css'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean
  onOpen: (value: boolean) => void
  campaign: Campaign
  userInCampaign: UserInDetails
  fetchPosts: any
}

const OverlayContentVideo = ({ open = false, onOpen, campaign, userInCampaign, fetchPosts }: Props) => {
  const [t] = useTranslation('overlayContentVideo')
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const visionService = useMemo(() => new VisionService(), [])
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [loadingMidias, setLoadingMidias] = useState(false)
  const [videos, setVideos] = useState<Array<Video>>([])
  const [videoSelected, setVideoSelected] = useState<Video>(null)

  logService.init('Overlay Content Video')

  const handleOpenEvent = useCallback(
    (event: boolean) => {
      if (onOpen && typeof onOpen === 'function') {
        onOpen(event)
      }
    },
    [onOpen]
  )

  const handleSubmit = useCallback(() => {
    if (videoSelected) {
      setLoadingSubmit(true)
      const canLinkVideo =
        videos.filter((m) => {
          return m?.uid === videoSelected?.id
        })?.length === 0
      if (canLinkVideo) {
        visionService
          .postMediaYoutubeIntoCampaign(videoSelected, campaign?._id)
          .then(() => {
            toast.success(t('success'), {
              closeButton: true,
              className: 'full text-bold',
              position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
              notOverClick: true,
              immediately: true,
            })
          })
          .then(() => fetchPosts())
          .catch((err: Error) => {
            logService.error({
              message: 'Error submitting media',
              error: err,
            })
            toast.error(t('unexpected'), {
              closeButton: true,
              className: 'full text-bold',
              position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
              notOverClick: true,
              immediately: true,
            })
          })
          .finally(() => {
            setLoadingSubmit(false)
          })
      }
      handleOpenEvent(false)
    }
  }, [campaign, videos, videoSelected, t, toast, handleOpenEvent, logService, visionService, fetchPosts])

  const handleGetMidias = useCallback(() => {
    setLoadingMidias(true)
    setVideoSelected(null)
    visionService
      .getYoutubeMidias(userInCampaign?.idNetwork)
      .then((result: Array<Video>) => {
        setLoadingMidias(false)
        setVideos(result)
      })
      .catch((err: any) => {
        setLoadingMidias(false)
        logService.error({
          message: 'Error get medias',
          error: err,
        })
      })
  }, [logService, userInCampaign, visionService, setLoadingMidias, setVideos])

  useEffect(() => {
    if (open) {
      handleGetMidias()
    }
  }, [open, handleGetMidias])

  if (!campaign?._id || !['youtube'].includes(campaign?.socialNetworks?.[0])) {
    return null
  }

  return (
    <Overlay
      open={open}
      onCloseChange={() => {
        handleOpenEvent(false)
      }}
      buttonClose={false}
      header={<Header loadingSubmit={loadingSubmit} onOpenEvent={handleOpenEvent} />}
      headerColor="var(--black_medium)"
      footer={<Footer loadingSubmit={loadingSubmit} videoSelected={videoSelected} onOpenEvent={handleOpenEvent} onSubmit={handleSubmit} />}
    >
      <div className="row">
        {loadingMidias ? (
          <div className="display-flex justify-content-center">
            <SqLoader size="big" />
          </div>
        ) : videos?.length ? (
          videos?.map((video, i) => (
            <SelectorImage
              index={i}
              media={video}
              key={video?.id}
              checked={video?.id === videoSelected?.id}
              mediaTitle={video?.snippet?.localized?.title}
              mediaURL={video?.snippet?.thumbnails?.medium?.url}
              onChange={(video) => setVideoSelected(video)}
              hasLabel={true}
              hasBorder={true}
            />
          ))
        ) : (
          <span className="empty-video">{t('emptyVideos')}</span>
        )}
      </div>
    </Overlay>
  )
}

export default OverlayContentVideo
